import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import VehiclesSubNav from "./VehiclesSubNav";
import { api, FUEL_TYPES, VEHICLE_STATUSES } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { Plus, Truck, Gauge, AlertTriangle, CalendarClock, Upload } from "lucide-react";
import BulkImportModal from "../../components/BulkImportModal";

const EMPTY_FORM = {
  regnr: "",
  brand: "",
  model: "",
  year: new Date().getFullYear(),
  fueltype: "diesel",
  consumption: "",
  odometer: "",
  responsible_user_id: "",
  inspection_org: "",
  workshop: "",
  next_inspection: "",
  next_service: "",
  status: "aktiv",
};

export default function VehiclesList() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", q: "" });
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.q) params.q = filter.q;
      const { data } = await api.get("/vehicles", { params });
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user, filter.status]);

  useEffect(() => {
    if (!user) return;
    api.get("/org/members").then((r) => setOrgUsers(r.data || [])).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <Layout>
        <div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div>
      </Layout>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        consumption: form.consumption ? Number(form.consumption) : null,
        odometer: form.odometer ? Number(form.odometer) : 0,
        responsible_user_id: form.responsible_user_id || null,
        next_inspection: form.next_inspection
          ? new Date(form.next_inspection).toISOString()
          : null,
        next_service: form.next_service
          ? new Date(form.next_service).toISOString()
          : null,
      };
      await api.post("/vehicles", payload);
      toast.success(t("veh.created"));
      setShowForm(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const overdue = (v) => v.next_inspection && v.next_inspection < new Date().toISOString();

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="label-tech">{t("veh.label")}</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("veh.tab.all")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImport(true)}
              className="btn-ghost"
              data-testid="vehicles-import-btn"
            >
              <Upload size={14} /> {t("bulk.import")}
            </button>
            <button
              onClick={() => setShowForm((s) => !s)}
              className="btn-primary"
              data-testid="vehicles-new-btn"
            >
              <Plus size={14} /> {t("veh.new")}
            </button>
          </div>
        </div>

        <VehiclesSubNav />

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <input
            placeholder={`${t("market.search")}: ${t("veh.regnr")} / ${t("veh.brand")}`}
            className="input-tech max-w-[260px]"
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            onBlur={() => load()}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="vehicles-search-input"
          />
          <select
            className="input-tech max-w-[200px]"
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            data-testid="vehicles-status-filter"
          >
            <option value="">{t("veh.status")}: —</option>
            {VEHICLE_STATUSES.map((s) => (
              <option key={s} value={s}>{t(`veh.status.${s}`)}</option>
            ))}
          </select>
        </div>

        {showForm && (
          <form
            onSubmit={submit}
            className="mb-8 border border-[var(--ink-200)] bg-[var(--bone)] p-5 grid grid-cols-1 md:grid-cols-3 gap-4"
            data-testid="vehicle-form"
          >
            <div className="md:col-span-3 label-tech">{t("veh.new")}</div>
            <div>
              <div className="label-tech mb-2">{t("veh.regnr")} *</div>
              <input className="input-tech" required value={form.regnr} onChange={(e) => setForm({ ...form, regnr: e.target.value.toUpperCase() })} data-testid="vehicle-regnr-input" />
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.brand")}</div>
              <input className="input-tech" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="vehicle-brand-input" />
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.model")}</div>
              <input className="input-tech" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="vehicle-model-input" />
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.year")}</div>
              <input type="number" className="input-tech" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.fueltype")}</div>
              <select className="input-tech" value={form.fueltype} onChange={(e) => setForm({ ...form, fueltype: e.target.value })} data-testid="vehicle-fuel-select">
                {FUEL_TYPES.map((f) => <option key={f} value={f}>{t(`veh.fuel.${f}`)}</option>)}
              </select>
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.consumption")}</div>
              <input type="number" step="0.1" className="input-tech" value={form.consumption} onChange={(e) => setForm({ ...form, consumption: e.target.value })} data-testid="vehicle-consumption-input" />
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.odometer")}</div>
              <input type="number" className="input-tech" value={form.odometer} onChange={(e) => setForm({ ...form, odometer: e.target.value })} />
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.responsible")}</div>
              <select className="input-tech" value={form.responsible_user_id} onChange={(e) => setForm({ ...form, responsible_user_id: e.target.value })} data-testid="vehicle-responsible-select">
                <option value="">{t("veh.responsiblePh")}</option>
                {orgUsers.map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.inspectionOrg")}</div>
              <input className="input-tech" value={form.inspection_org} onChange={(e) => setForm({ ...form, inspection_org: e.target.value })} />
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.workshop")}</div>
              <input className="input-tech" value={form.workshop} onChange={(e) => setForm({ ...form, workshop: e.target.value })} />
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.nextInspection")}</div>
              <input type="date" className="input-tech" value={form.next_inspection} onChange={(e) => setForm({ ...form, next_inspection: e.target.value })} data-testid="vehicle-next-inspection-input" />
            </div>
            <div>
              <div className="label-tech mb-2">{t("veh.nextService")}</div>
              <input type="date" className="input-tech" value={form.next_service} onChange={(e) => setForm({ ...form, next_service: e.target.value })} data-testid="vehicle-next-service-input" />
            </div>
            <div className="md:col-span-3 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>
                {t("admin.orgs.cancel")}
              </button>
              <button type="submit" className="btn-primary" data-testid="vehicle-submit-btn">
                {t("admin.orgs.save")}
              </button>
            </div>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          {loading ? (
            <div className="bg-[var(--surface)] p-6 label-tech">…</div>
          ) : items.length === 0 ? (
            <div className="bg-[var(--surface)] p-10 label-tech md:col-span-2 lg:col-span-3">{t("veh.empty")}</div>
          ) : items.map((v) => (
            <Link key={v.id} to={`/vehicles/${v.id}`} className="bg-[var(--surface)] p-5 hover:bg-[var(--surface-alt)] transition-colors block" data-testid={`vehicle-card-${v.id}`}>
              <div className="flex items-center gap-2 label-tech"><Truck size={12} /> {t(`veh.fuel.${v.fueltype}`)} · {v.year || "—"}</div>
              <div className="font-display text-lg mt-2 font-mono-tech">{v.regnr}</div>
              <div className="text-sm text-[var(--ink-700)] mt-1">{v.brand} {v.model}</div>
              <div className="mt-3 flex items-center gap-3 text-xs text-[var(--ink-500)]">
                <span className="inline-flex items-center gap-1"><Gauge size={12} /> {Math.round(v.odometer || 0).toLocaleString()} km</span>
                {overdue(v) && (
                  <span className="inline-flex items-center gap-1 text-[var(--brick)]" data-testid={`vehicle-overdue-${v.id}`}>
                    <AlertTriangle size={12} /> {t("veh.outOfCompliance")}
                  </span>
                )}
                {!overdue(v) && v.next_inspection && (
                  <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> {v.next_inspection.slice(0, 10)}</span>
                )}
              </div>
            </Link>
          ))}
        </div>

        {showImport && (
          <BulkImportModal
            title={t("bulk.title.vehicles")}
            description={t("bulk.desc.vehicles")}
            uploadEndpoint="/vehicles/bulk-import"
            templateEndpoint="/vehicles/bulk-import/template"
            onClose={() => setShowImport(false)}
            onSuccess={() => load()}
          />
        )}
      </div>
    </Layout>
  );
}
